import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import {
  BanknotesIcon,
  BoltIcon,
  BuildingLibraryIcon,
  ChevronDownIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

import BasketTable from "../../components/basket/BasketTable";
import BasketUpsells, {
  DEFAULT_BASKET_UPSELLS,
  IBasketUpsell,
} from "../../components/basket/BasketUpsells";
import BasketTermsAndConditions from "../../components/basket/BasketTermsAndConditions";
import CollapsibleSection from "../../components/shared/CollapsibleSection";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import FullscreenLoading from "../../components/shared/LoadingWheelFullscreen";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useBaskets from "../../data/useBaskets";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { IAccount } from "../../models/account/IAccount";
import useNotification from "../../components/notifications/useNotifications";
import useShippingPolicies from "../../data/useShippingPolicies";
import { IShippingPolicy } from "../../models/ShippingPolicy";
import BasketInvoiceInputs from "./BasketInvoiceInputs";
import { PAYMENT_METHODS } from "./paymentMethods";
import { FormatType } from "../../models/EbayOffer";
import { DiscountType, IBasketItem } from "../../models/Basket";


export default function Basket() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { register, setValue, getValues, watch, reset } = useForm({
    defaultValues: {
      paymentMethod: 1,
      accountId: 0,
      shippingPolicyId: "0",
      notes: "",
      address: {
        line1: "",
        line2: "",
        line3: "",
        line4: "",
        locality: "",
        town: "",
        county: "",
      },
      addTaxToTotal: false,
      email: "",
      mobile: "",
    }
  });
  const { getBasket, clearBasket, checkout } = useBaskets("Basket");
  const { shippingPolicies: allShippingPolicies } = useShippingPolicies();
  const [selectedAccount, setSelectedAccount] = useState<IAccount | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [rowDiscounts, setRowDiscounts] = useState<Record<string, number>>({});
  const [rowShippingPolicyIds, setRowShippingPolicyIds] = useState<
    Record<string, string>
  >({});
  const [noInvoiceItemIds, setNoInvoiceItemIds] = useState<Set<string>>(
    new Set()
  );

  const toggleNoInvoiceItem = (id: string) => {
    setNoInvoiceItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [selectedUpsellId, setSelectedUpsellId] = useState<string | null>(null);
  const [termsConfirmed, setTermsConfirmed] = useState(false);
  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(true);
  // Last-used comparison reg lives outside the form because the input now
  // only exists inside VehicleCompareModal; we just remember it across opens.
  const [customerVrm, setCustomerVrm] = useState("");

  // TODO: load upsells from admin settings once endpoint exists.
  const availableUpsells: IBasketUpsell[] = DEFAULT_BASKET_UPSELLS;
  const selectedUpsell = useMemo(
    () => availableUpsells.find((u) => u.id === selectedUpsellId) ?? null,
    [availableUpsells, selectedUpsellId]
  );

  const setRowDiscount = (id: string, pct: number) => {
    setRowDiscounts((prev) => {
      const next = { ...prev };
      const clamped = Math.min(100, Math.max(0, pct || 0));
      if (clamped === 0) {
        delete next[id];
      } else {
        next[id] = clamped;
      }
      return next;
    });
  };

  const setRowShippingPolicyId = (id: string, policyId: string) => {
    setRowShippingPolicyIds((prev) => {
      const next = { ...prev };
      if (!policyId) {
        delete next[id];
      } else {
        next[id] = policyId;
      }
      return next;
    });
  };
  // const [shippingPolicies, setShippingPolicies] = useState<Record<string, number | string>[]>([]);

  const addTaxToTotal = watch("addTaxToTotal");
  const customerEmail = watch("email");
  const customerMobile = watch("mobile");
  const paymentMethod = watch("paymentMethod");

  const basketItems = getBasket.data?.data.items ?? [];
  const allItemsNoInvoice =
    basketItems.length > 0 &&
    basketItems.every((it) => noInvoiceItemIds.has(it.id));

  const getItemPrice = (item: IBasketItem) => {
    // Use basket price if set, otherwise calculate default price
    if (item.basketPrice !== undefined && item.basketPrice !== null) {
      return Number(item.basketPrice) || 0;
    }

    // Fall back to calculating default price
    if (item.part.offers && item.part.offers.length > 0) {
      const offer = item.part.offers[0];
      if (offer.format === FormatType.FixedPrice) {
        return parseFloat(offer.buyItNowPrice?.toString() || "0") || 0;
      } else {
        return parseFloat(offer.auctionStartPrice?.toString() || "0") || 0;
      }
    }

    return parseFloat(item.part.enteredPrice?.toString() || "0") || 0;
  };

  // Don't need now it's a drawer - if we ever use this as a page too then we can add in conditionally
  // useEffect(() => {
  //   setInfo({
  //     name: "Basket",
  //     desc: "View and manage your current basket",
  //   });
  // }, [])

  useEffect(() => {
    if (!getBasket.isLoading && !allShippingPolicies.isLoading) {
      setIsLoading(false);
    }
  }, [
    getBasket.isLoading,
    getBasket.data,
    allShippingPolicies.isLoading,
    allShippingPolicies.data,
  ]);


  const handleSubmit = () => {

    if (!selectedAccount) {
      addNotification({
        primaryText: "No Account Selected",
        variant: "warn"
      });
      return;
    }

    const data = getValues();

    if (itemsMissingShipping > 0) {
      addNotification({
        primaryText: "Shipping Method Missing",
        secondaryText: `${itemsMissingShipping} item${itemsMissingShipping === 1 ? "" : "s"} ${itemsMissingShipping === 1 ? "has" : "have"} no shipping method selected.`,
        variant: "warn",
      });
      return;
    }

    if (!termsConfirmed) {
      addNotification({
        primaryText: "Terms & Conditions Not Confirmed",
        secondaryText: "Tick the confirmation that the T&Cs have been read to the customer.",
        variant: "warn",
      });
      return;
    }

    setIsSubmitLoading(true);

    const body = {
      accountId: selectedAccount.id,
      paymentMethod: data.paymentMethod,
      // shippingPolicyId is kept for backwards compat; per-row mapping is the
      // source of truth now (see itemShipping below).
      shippingPolicyId: Object.values(rowShippingPolicyIds)[0] ?? "0",
      // TODO: persist per-item shipping selections on the backend once schema is ready.
      itemShipping: rowShippingPolicyIds,
      notes: data.notes,
      email: data.email,
      mobile: data.mobile,
      addTaxToTotal: data.addTaxToTotal,
      // Per-row exclusions from the printed invoice — see
      // docs/pending-endpoints.md ("Basket per-item invoice exclusion").
      noInvoiceItemIds: Array.from(noInvoiceItemIds),
      discountType: DiscountType.Flat,
      discountValue: 0,
      // TODO: persist upsell selection on the backend once schema is ready.
      upsell: selectedUpsell
        ? {
            id: selectedUpsell.id,
            label: selectedUpsell.label,
            percentage: selectedUpsell.percentage,
            amount: upsellAmount,
          }
        : null,
      termsConfirmed,
      ...data.address,
    }

    checkout.mutate(body, {
      onSuccess: (file) => {
        // If every basket item is flagged "No Invoice", there's nothing to
        // print so we skip the PDF download entirely.
        if (!allItemsNoInvoice && file) {
          const url = window.URL.createObjectURL(file);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `invoice-${getBasket.data?.data.id}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        if (allItemsNoInvoice) {
          addNotification({
            primaryText: "Order completed",
            secondaryText: "No invoice was generated — all items were flagged.",
            variant: "success",
          });
        } else if (noInvoiceItemIds.size > 0) {
          addNotification({
            primaryText: "Order completed",
            secondaryText: `${noInvoiceItemIds.size} item${
              noInvoiceItemIds.size === 1 ? " was" : "s were"
            } excluded from the invoice.`,
            variant: "success",
          });
        }

        reset();
        setNoInvoiceItemIds(new Set());
      },
      onSettled: () => {
        setIsSubmitLoading(false);
        setIsConfirmOpen(false);
      }
    });
  };

  const isEmpty = useMemo(() => {
    return getBasket.data?.data.items && getBasket.data?.data.items.length === 0;
  }, [getBasket.data?.data.items]);

  // Placeholder secondary actions. Backend endpoints don't exist yet — see
  // docs/pending-endpoints.md ("Basket secondary actions"). When they ship,
  // these handlers should call the relevant mutations.
  const requireAccountAndItems = (action: string) => {
    if (isEmpty) {
      addNotification({
        primaryText: `Cannot ${action}`,
        secondaryText: "Basket is empty.",
        variant: "warn",
      });
      return false;
    }
    if (!selectedAccount) {
      addNotification({
        primaryText: `Cannot ${action}`,
        secondaryText: "Select a customer account first.",
        variant: "warn",
      });
      return false;
    }
    return true;
  };

  const handleGenerateQuote = () => {
    if (!requireAccountAndItems("generate quote")) return;
    addNotification({
      primaryText: "Quote generation coming soon",
      secondaryText:
        "Backend endpoint pending — see docs/pending-endpoints.md.",
      variant: "info",
    });
  };

  const handleGenerateInvoice = () => {
    if (!requireAccountAndItems("generate invoice")) return;
    addNotification({
      primaryText: "Invoice-only generation coming soon",
      secondaryText:
        "Backend endpoint pending — use Submit to generate an invoice as part of checkout.",
      variant: "info",
    });
  };

  const handleHoldParts = (hours: 24 | 48) => {
    if (!requireAccountAndItems(`hold parts for ${hours} hours`)) return;
    addNotification({
      primaryText: `Hold for ${hours} hours coming soon`,
      secondaryText:
        "Backend endpoint pending — see docs/pending-endpoints.md.",
      variant: "info",
    });
  };

  const shippingPolicyList: IShippingPolicy[] = useMemo(
    () => allShippingPolicies.data?.data ?? [],
    [allShippingPolicies.data]
  );

  const policyById = useMemo(() => {
    const map: Record<string, IShippingPolicy> = {};
    for (const p of shippingPolicyList) map[p.id] = p;
    return map;
  }, [shippingPolicyList]);

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
  const itemTotals = useMemo(() => {
    const totals = (getBasket.data?.data.items ?? []).reduce(
      (acc, item) => {
        const rawPrice = getItemPrice(item);
        const rowDiscountPct = Math.min(100, Math.max(0, rowDiscounts[item.id] ?? 0));
        const inputPrice = rawPrice * (1 - rowDiscountPct / 100);
        const grossTaxRate = Math.min(Math.max(Number(item.taxRate) || 0, 0), 100) / 100;

        // AddTaxToTotal=true mode: input is treated as net.
        acc.subtotalWithTaxAdded += inputPrice;
        acc.taxWithTaxAdded += inputPrice * grossTaxRate;

        // AddTaxToTotal=false mode: input is treated as gross and split into net+tax.
        acc.grossWithTaxIncluded += inputPrice;
        acc.subtotalWithTaxIncluded += inputPrice * (1 - grossTaxRate);
        acc.taxWithTaxIncluded += inputPrice * grossTaxRate;

        return acc;
      },
      {
        subtotalWithTaxAdded: 0,
        taxWithTaxAdded: 0,
        grossWithTaxIncluded: 0,
        subtotalWithTaxIncluded: 0,
        taxWithTaxIncluded: 0,
      }
    );

    return {
      subtotalWithTaxAdded: round2(totals.subtotalWithTaxAdded),
      taxWithTaxAdded: round2(totals.taxWithTaxAdded),
      grossWithTaxIncluded: round2(totals.grossWithTaxIncluded),
      subtotalWithTaxIncluded: round2(totals.subtotalWithTaxIncluded),
      taxWithTaxIncluded: round2(totals.taxWithTaxIncluded),
    };
  }, [getBasket.data?.data.items, rowDiscounts]);

  const itemSubtotal = addTaxToTotal
    ? itemTotals.subtotalWithTaxAdded
    : itemTotals.subtotalWithTaxIncluded;
  const itemTax = addTaxToTotal
    ? itemTotals.taxWithTaxAdded
    : itemTotals.taxWithTaxIncluded;
  const itemTotalBeforeDiscount = addTaxToTotal
    ? itemTotals.subtotalWithTaxAdded + itemTotals.taxWithTaxAdded
    : itemTotals.grossWithTaxIncluded;

  // Per-row discounts are baked into itemTotals; no global discount applied.
  const discountTotal = 0;
  const basketSubtotal = round2(itemSubtotal);
  const basketTax = round2(itemTax);

  const shippingPrice = useMemo(() => {
    const items = getBasket.data?.data.items ?? [];
    let total = 0;
    for (const item of items) {
      const policyId = rowShippingPolicyIds[item.id];
      if (!policyId) continue;
      const policy = policyById[policyId];
      if (policy) total += policy.price;
    }
    return round2(total);
  }, [getBasket.data?.data.items, rowShippingPolicyIds, policyById]);

  const itemsMissingShipping = useMemo(() => {
    const items = getBasket.data?.data.items ?? [];
    return items.filter((it) => !rowShippingPolicyIds[it.id]).length;
  }, [getBasket.data?.data.items, rowShippingPolicyIds]);

  const purchasePriceForUpsell = round2(itemTotalBeforeDiscount - discountTotal);
  const upsellAmount = selectedUpsell
    ? round2(purchasePriceForUpsell * (selectedUpsell.percentage / 100))
    : 0;
  const finalTotal = round2(
    itemTotalBeforeDiscount - discountTotal + shippingPrice + upsellAmount
  );

  if (isLoading) {
    return <FullscreenLoading />;
  }

  return (
    <>
      <ConfirmModal open={isConfirmOpen} setOpen={setIsConfirmOpen}
        title={"Are you sure you want sell these items?"}
        message={
          allItemsNoInvoice
            ? "Parts will be withdrawn from eBay and marked as sold and money received. No invoice will be generated — all items are flagged 'No Invoice'."
            : noInvoiceItemIds.size > 0
            ? `Parts will be withdrawn from eBay and marked as sold and money received. ${noInvoiceItemIds.size} item${noInvoiceItemIds.size === 1 ? "" : "s"} will be excluded from the invoice.`
            : "Parts will be withdrawn from eBay and marked as sold and money received."
        }
        confirmButtonText={"Submit"}
        onConfirm={handleSubmit}
        isLoading={isSubmitLoading}
      />
      <form className="flex flex-col w-full gap-4">
        {/* 1. Customer details (collapsible) */}
        <div className="rounded-md border border-gray-200">
          <button
            type="button"
            onClick={() => setCustomerDetailsOpen((p) => !p)}
            aria-expanded={customerDetailsOpen}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <div className="flex items-center gap-2">
              <UserCircleIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-gray-800">Customer Details</p>
                <p className="text-xs text-gray-500">
                  {selectedAccount
                    ? `Account: ${selectedAccount.name}`
                    : "No account selected"}
                  {customerEmail ? ` · ${customerEmail}` : ""}
                  {customerMobile ? ` · ${customerMobile}` : ""}
                  {customerVrm ? ` · Reg: ${customerVrm.toUpperCase()}` : ""}
                  {noInvoiceItemIds.size > 0
                    ? ` · ${noInvoiceItemIds.size} excluded from invoice`
                    : ""}
                </p>
              </div>
            </div>
            <ChevronDownIcon
              className={classNames(
                "h-5 w-5 text-gray-500 transition-transform duration-200",
                customerDetailsOpen ? "" : "-rotate-90"
              )}
              aria-hidden="true"
            />
          </button>
          <CollapsibleSection isOpen={customerDetailsOpen} className="border-t border-gray-200">
            <div className="flex flex-col gap-4 p-4">
              <BasketInvoiceInputs
                register={register}
                setValue={setValue}
                watch={watch}
                setSelectedAccount={setSelectedAccount}
                selectedAccount={selectedAccount}
              />
            </div>
          </CollapsibleSection>
        </div>

        {/* 2. Basket items */}
        {isEmpty ? (
          <p className="mt-8 text-center text-xl text-gray-700">Your basket is empty</p>
        ) :
          <BasketTable
            data={getBasket.data?.data.items ?? []}
            customerVrm={customerVrm}
            onCustomerVrmChange={setCustomerVrm}
            addTaxToTotal={addTaxToTotal}
            rowDiscounts={rowDiscounts}
            onRowDiscountChange={setRowDiscount}
            shippingPolicies={shippingPolicyList}
            rowShippingPolicyIds={rowShippingPolicyIds}
            onRowShippingChange={setRowShippingPolicyId}
            noInvoiceItemIds={Array.from(noInvoiceItemIds)}
            onNoInvoiceToggle={toggleNoInvoiceItem}
          />
        }

        {/* 3. Upsells */}
        <BasketUpsells
          options={availableUpsells}
          selectedId={selectedUpsellId}
          onSelect={setSelectedUpsellId}
          purchasePrice={purchasePriceForUpsell}
          disabled={isEmpty}
        />

        {/* 4. Terms & Conditions */}
        <BasketTermsAndConditions
          confirmed={termsConfirmed}
          onConfirmedChange={setTermsConfirmed}
        />

        {/* Totals */}
        <div className="flex justify-end gap-4">
          <div className="shrink-0">
            <p className="text-right text-xl text-gray-700">Total: {getBasket.data?.data.items?.length} items</p>
            <p className="text-right text-xl text-gray-700">Subtotal: {PriceFormat(basketSubtotal)}</p>
            <p className="text-right text-xl text-gray-700">Total Tax: {PriceFormat(basketTax)}</p>
            <p className="text-right text-xl text-gray-700">Shipping: {PriceFormat(shippingPrice)}</p>
            {selectedUpsell && (
              <p className="text-right text-xl text-primary-700">
                {selectedUpsell.label} ({selectedUpsell.percentage}%): +{PriceFormat(upsellAmount)}
              </p>
            )}
            <p className="text-right text-2xl font-semibold text-gray-900">Total Price: {PriceFormat(finalTotal)}</p>
          </div>
        </div>

        <PaymentMethodBar
          value={paymentMethod}
          onChange={(id) => setValue("paymentMethod", id)}
        />

        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <ButtonInput
            label="Clear"
            classes={classNames(isEmpty ? "" : "!bg-red-600 hover:!bg-red-700")}
            onClick={() => clearBasket.mutate()}
            disabled={isEmpty}
          />

          <div className="flex flex-wrap items-center justify-end gap-2">
            <SecondaryActionButton
              label="Generate Quote"
              icon={DocumentTextIcon}
              onClick={handleGenerateQuote}
              disabled={isEmpty || !selectedAccount}
              tooltip={
                !selectedAccount
                  ? "Select an account first"
                  : "Generate a customer quote PDF (no sale)"
              }
            />
            <SecondaryActionButton
              label="Generate Invoice"
              icon={DocumentArrowDownIcon}
              onClick={handleGenerateInvoice}
              disabled={isEmpty || !selectedAccount}
              tooltip={
                !selectedAccount
                  ? "Select an account first"
                  : "Generate an invoice PDF without completing checkout"
              }
            />
            <SecondaryActionButton
              label="Hold 24h"
              icon={ClockIcon}
              onClick={() => handleHoldParts(24)}
              disabled={isEmpty || !selectedAccount}
              tooltip={
                !selectedAccount
                  ? "Select an account first"
                  : "Reserve these parts for 24 hours"
              }
            />
            <SecondaryActionButton
              label="Hold 48h"
              icon={ClockIcon}
              onClick={() => handleHoldParts(48)}
              disabled={isEmpty || !selectedAccount}
              tooltip={
                !selectedAccount
                  ? "Select an account first"
                  : "Reserve these parts for 48 hours"
              }
            />
            <ButtonInput
              label="Submit"
              classes={undefined}
              onClick={() => setIsConfirmOpen(true)}
              disabled={isEmpty || !termsConfirmed}
            />
          </div>
        </div>
      </form>
    </>
  );
}

const PAYMENT_METHOD_ICONS: Record<number, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  0: BanknotesIcon, // Cash
  1: CreditCardIcon, // Card
  3: BoltIcon, // Stripe
  4: BuildingLibraryIcon, // Bank Transfer
  5: PencilSquareIcon, // Cheque
};

function PaymentMethodBar({
  value,
  onChange,
}: {
  value: number;
  onChange: (id: number) => void;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <CreditCardIcon className="h-4 w-4 text-primary-600" aria-hidden="true" />
          Payment Method
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {PAYMENT_METHODS.map((method) => {
            const isActive = Number(value) === method.id;
            const Icon = PAYMENT_METHOD_ICONS[method.id];
            return (
              <button
                key={method.id}
                type="button"
                onClick={() => onChange(method.id)}
                aria-pressed={isActive}
                className={classNames(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-primary-500",
                  isActive
                    ? "border-primary-500 bg-primary-600 text-white shadow-sm hover:bg-primary-700"
                    : "border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                )}
              >
                {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
                <span>{method.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface SecondaryActionButtonProps {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
}

function SecondaryActionButton({
  label,
  icon: Icon,
  onClick,
  disabled,
  tooltip,
}: SecondaryActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip ?? label}
      aria-label={label}
      className={classNames(
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary-500",
        disabled
          ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
          : "border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}